import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  CircleNotch,
  CalendarBlank,
  ArrowsClockwise,
  Lightning,
  Sun,
} from "@phosphor-icons/react";

const TZ = "Europe/Madrid";
// Timeline visible hour range (full day, so post-midnight manual franjas are visible)
const HOUR_START = 0;
const HOUR_END = 24;
const HOUR_SPAN = HOUR_END - HOUR_START;

function dateKey(iso) {
  // Convert an ISO datetime to a YYYY-MM-DD key in Europe/Madrid
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  return `${y}-${m}-${day}`;
}

function formatDayLabel(dateStr) {
  // dateStr is YYYY-MM-DD (local Madrid date)
  const [y, m, d] = dateStr.split("-").map((s) => parseInt(s, 10));
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0)); // noon UTC to avoid tz drift
  return new Intl.DateTimeFormat("es-ES", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  }).format(dt);
}

function hoursFromIso(iso) {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("es-ES", {
    timeZone: TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const h = parseInt(parts.find((p) => p.type === "hour").value, 10);
  const mi = parseInt(parts.find((p) => p.type === "minute").value, 10);
  return h + mi / 60;
}

function timeLabel(iso) {
  const d = new Date(iso);
  return new Intl.DateTimeFormat("es-ES", {
    timeZone: TZ,
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

// Build an array of 7 date-strings starting from today (Madrid tz)
function nextSevenDates() {
  const today = new Date();
  const result = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(today.getTime() + i * 86400000);
    result.push(dateKey(d.toISOString()));
  }
  return result;
}

function BookingBar({ booking, court }) {
  const startH = Math.max(HOUR_START, hoursFromIso(booking.start));
  const endH = Math.min(HOUR_END, hoursFromIso(booking.end));
  if (endH <= startH) return null;
  const leftPct = ((startH - HOUR_START) / HOUR_SPAN) * 100;
  const widthPct = ((endH - startH) / HOUR_SPAN) * 100;
  const isAmbient = booking.is_ambient || court?.lighting_mode === "ambient";
  const isManual = booking.is_manual;
  const disabled = booking.override_disabled;
  const skipped = booking.skipped_reason;

  let bg = "bg-[#CCFF00] text-black"; // normal booking
  if (isAmbient) bg = "bg-cyan-400/80 text-black";
  if (isManual) bg = "bg-cyan-400 text-black ring-2 ring-cyan-200/60"; // manual: distinct ring
  if (skipped) bg = "bg-zinc-700 text-zinc-300";
  if (disabled) bg = "bg-red-500/40 text-red-100 border border-red-500/60";

  const label = isManual
    ? `M · ${timeLabel(booking.start)}–${timeLabel(booking.end)}`
    : `${timeLabel(booking.start)}–${timeLabel(booking.end)}`;

  return (
    <div
      data-testid={`week-bar-${booking.court_id}-${booking.start}`}
      className={`absolute top-1 bottom-1 rounded px-1.5 flex items-center overflow-hidden text-[10px] font-semibold ${bg}`}
      style={{ left: `${leftPct}%`, width: `${widthPct}%`, minWidth: "22px" }}
      title={`${booking.resource_name} · ${timeLabel(booking.start)} - ${timeLabel(booking.end)}${isManual ? " (manual)" : ""}`}
    >
      <span className="truncate">{label}</span>
    </div>
  );
}

function DayRow({ dateStr, bookings, courts }) {
  const bookingsByCourt = useMemo(() => {
    const m = {};
    for (const b of bookings) {
      if (!m[b.court_id]) m[b.court_id] = [];
      m[b.court_id].push(b);
    }
    return m;
  }, [bookings]);

  // Only show courts that have bookings that day, ordered by court.order
  const orderedCourts = useMemo(() => {
    const ids = Object.keys(bookingsByCourt);
    return courts
      .filter((c) => ids.includes(c.id))
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }, [bookingsByCourt, courts]);

  const totalBookings = bookings.filter((b) => !b.is_ambient).length;
  const ambientCount = bookings.filter((b) => b.is_ambient).length;

  return (
    <section
      data-testid={`week-day-${dateStr}`}
      className="bg-[#121212] border border-zinc-800 rounded-md overflow-hidden"
    >
      <header className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase font-mono">
            {formatDayLabel(dateStr).split(",")[0]}
          </div>
          <div className="text-sm sm:text-base font-display font-black capitalize mt-0.5">
            {formatDayLabel(dateStr)}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">Reservas</div>
            <div className="text-lg font-display font-black text-[#CCFF00]">{totalBookings}</div>
          </div>
          {ambientCount > 0 && (
            <div className="text-right hidden sm:block">
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">Ambient</div>
              <div className="text-lg font-display font-black text-cyan-400">{ambientCount}</div>
            </div>
          )}
        </div>
      </header>

      {orderedCourts.length === 0 ? (
        <div className="px-4 py-6 text-sm text-zinc-500 italic">Sin reservas</div>
      ) : (
        <div className="px-3 sm:px-4 py-3">
          {/* Hour scale */}
          <div className="relative h-4 mb-2 hidden sm:block">
            <div className="absolute inset-0 flex">
              {Array.from({ length: HOUR_SPAN + 1 }).map((_, i) => (
                <div
                  key={i}
                  className="flex-1 border-l border-zinc-800/60 relative"
                  style={i === HOUR_SPAN ? { borderRight: "1px solid rgb(39 39 42 / 0.6)" } : undefined}
                >
                  {(HOUR_START + i) % 3 === 0 && (
                    <span className="absolute -top-0.5 -left-1.5 text-[9px] text-zinc-600 font-mono">
                      {HOUR_START + i}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Court rows */}
          <div className="space-y-1.5">
            {orderedCourts.map((court) => (
              <div
                key={court.id}
                className="flex items-center gap-2 sm:gap-3"
                data-testid={`week-row-${dateStr}-${court.id}`}
              >
                <div className="w-24 sm:w-32 shrink-0 text-[11px] sm:text-xs text-zinc-300 truncate flex items-center gap-1.5">
                  {court.lighting_mode === "ambient" ? (
                    <Sun size={12} weight="bold" className="text-cyan-400 shrink-0" />
                  ) : (
                    <Lightning size={12} weight="bold" className="text-[#CCFF00] shrink-0" />
                  )}
                  {court.name}
                </div>
                <div className="relative flex-1 h-6 bg-zinc-900/60 rounded border border-zinc-800/60">
                  {/* Grid ticks */}
                  <div className="absolute inset-0 flex pointer-events-none">
                    {Array.from({ length: HOUR_SPAN }).map((_, i) => (
                      <div key={i} className="flex-1 border-l border-zinc-800/40" />
                    ))}
                  </div>
                  {(bookingsByCourt[court.id] || []).map((b, idx) => (
                    <BookingBar key={idx} booking={b} court={court} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile hour hints */}
          <div className="flex justify-between mt-2 sm:hidden text-[9px] text-zinc-600 font-mono">
            <span>{HOUR_START}:00</span>
            <span>{Math.round((HOUR_START + HOUR_END) / 2)}:00</span>
            <span>{HOUR_END}:00</span>
          </div>
        </div>
      )}
    </section>
  );
}

export default function Week() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const data = await api.dashboard();
      setData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.syncNow();
      const startedAt = Date.now();
      while (Date.now() - startedAt < 90000) {
        await new Promise((r) => setTimeout(r, 2000));
        const s = await api.syncStatus();
        if (!s.running) break;
      }
      await load();
    } catch (err) {
      console.error(err);
    } finally {
      setSyncing(false);
    }
  };

  const days = useMemo(() => nextSevenDates(), []);

  const bookingsByDay = useMemo(() => {
    const out = Object.fromEntries(days.map((d) => [d, []]));
    if (!data?.bookings) return out;
    for (const b of data.bookings) {
      const k = dateKey(b.start);
      if (out[k]) out[k].push(b);
    }
    // Sort each day by start time
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => a.start.localeCompare(b.start));
    }
    return out;
  }, [data, days]);

  const totalWeekBookings = useMemo(
    () => (data?.bookings || []).filter((b) => !b.is_ambient).length,
    [data]
  );

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <CircleNotch size={28} className="animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="week-page">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase font-mono">
            Semana completa
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-black mt-1">
            Próximos 7 días
          </h1>
          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2">
            <CalendarBlank size={14} weight="bold" className="text-[#CCFF00]" />
            <span>
              <span className="text-[#CCFF00] font-bold">{totalWeekBookings}</span>{" "}
              reservas · {data?.scheduled_events_count ?? 0} eventos programados
            </span>
          </div>
        </div>
        <button
          data-testid="week-sync-button"
          onClick={handleSync}
          disabled={syncing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[#CCFF00] text-black text-sm font-bold hover:bg-[#B8E600] disabled:opacity-60 transition-colors self-start sm:self-auto"
        >
          <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} />
          {syncing ? "Sincronizando…" : "Sincronizar"}
        </button>
      </header>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[11px] text-zinc-400" data-testid="week-legend">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-[#CCFF00]" /> Reserva
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-cyan-400/80" /> Ambient (Parking/Terraza)
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-cyan-400 ring-2 ring-cyan-200/60" /> Manual (M)
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-zinc-700 border border-zinc-600" /> Sin luces
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-red-500/40 border border-red-500/60" /> Deshabilitada
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {days.map((d) => (
          <DayRow
            key={d}
            dateStr={d}
            bookings={bookingsByDay[d]}
            courts={data?.courts || []}
          />
        ))}
      </div>
    </div>
  );
}
